/**
 * Per-clinician isolation of SMART patient-context state on a shared browser
 * (Requirement 2). The cross-tab recovery cache lives in localStorage, which is
 * shared across every tab of a browser profile — so on a shared workstation the
 * most-recently-launched patient could otherwise surface (even as a demographics
 * hint on the "Resume patient?" screen) to a different clinician.
 *
 * We deliberately do NOT namespace the storage keys by subject: the SMART launch
 * session id is written from the URL *before* MarchAgent auth completes (the
 * "sign in to continue" block runs after capture), so a subject-scoped key would
 * mismatch across the login boundary and strand a legitimate launch. Instead we
 * detect a change of authenticated clinician and wipe the prior clinician's
 * SMART state. The server-side owner binding (see smart_fhir/routes.py) is the
 * authoritative PHI boundary; this closes the local UI-state leak.
 */

import { decodeJwtPayload } from '../auth/jwtPayload';
import { getStoredAccessToken } from '../auth/tokenStore';

const CLINICIAN_MARKER_KEY = 'march:smart:lastClinician';

const SMART_STORAGE_BASE_KEYS = [
  'march_smart_session_id',
  'march_smart_workspace_patient',
  'marchagent:pendingSmartLaunch',
  'march:smart:activeContext',
];

function safeSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Stable per-clinician key derived from the Keycloak subject of the current
 * access token. Empty when unauthenticated (dev/demo, or mid-login) — callers
 * must treat empty as "identity not yet known" and skip reconciliation.
 */
export function currentClinicianKey(): string {
  try {
    return (decodeJwtPayload(getStoredAccessToken())?.sub ?? '').trim();
  } catch {
    return '';
  }
}

function isSmartStorageKey(key: string): boolean {
  return SMART_STORAGE_BASE_KEYS.some((base) => key === base || key.startsWith(`${base}::`));
}

function wipeSmartKeys(store: Storage | null): void {
  if (!store) return;
  const toRemove: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && isSmartStorageKey(key)) toRemove.push(key);
  }
  for (const key of toRemove) {
    try {
      store.removeItem(key);
    } catch {
      // Best-effort teardown.
    }
  }
}

/** True when this tab's URL carries a fresh EHR launch context. */
function hasLaunchContextInUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URL(window.location.href).searchParams;
    const directSession =
      (params.get('session_id') ?? '').trim() || (params.get('smart_session_id') ?? '').trim();
    const smartStatus = (params.get('smart') ?? '').trim().toLowerCase();
    return Boolean(directSession) || smartStatus === 'connected';
  } catch {
    return false;
  }
}

/**
 * Clear the prior clinician's SMART state when the authenticated clinician on
 * this browser has changed since the last visit. Returns true when a switch was
 * detected and state was cleared.
 *
 * - The cross-tab localStorage recovery cache is always cleared on a switch.
 * - The per-tab sessionStorage session is cleared too, UNLESS this tab is a
 *   fresh EHR launch (URL launch context) — that session belongs to the new
 *   clinician and must survive.
 *
 * Safe to call on every entry: a no-op when identity is unknown or unchanged.
 */
export function reconcileClinicianScope(): boolean {
  const current = currentClinicianKey();
  if (!current) return false;

  const local = safeLocalStorage();
  const previous = (local?.getItem(CLINICIAN_MARKER_KEY) ?? '').trim();

  let switched = false;
  if (previous && previous !== current) {
    wipeSmartKeys(local);
    if (!hasLaunchContextInUrl()) {
      wipeSmartKeys(safeSessionStorage());
    }
    switched = true;
  }

  if (previous !== current) {
    try {
      local?.setItem(CLINICIAN_MARKER_KEY, current);
    } catch {
      // Best-effort marker.
    }
  }

  return switched;
}
